import { useMemo, useState } from "react";
import { formatMoney } from "@shared/roi/format";
import type { ScenarioModel, YearResult } from "@shared/roi/types";
import {
  AxisChrome,
  ChartEmptyState,
  ChartFrame,
  ChartLegend,
  ChartTooltip,
  HoverSlot,
  YearTick,
  ZeroLine,
  type LegendItem,
  type TooltipRow,
} from "./ChartChrome";
import { buildBreakdown, type BreakdownSeries, type BreakdownSlice } from "./breakdown";
import {
  axisTicks,
  axisTop,
  BAR_GAP,
  PAD_L,
  PAD_T,
  PLOT_H,
  PLOT_W,
  STACK_MAX,
  tooltipLeftPct,
} from "./geometry";

/**
 * A package earns and costs in the same hue, so it is named once; a line-item
 * group's tint only means anything next to the side it belongs to, so it says
 * which side it is.
 */
function legendItems(series: BreakdownSeries[]): LegendItem[] {
  const seen = new Set<string>();
  const items: LegendItem[] = [];
  for (const entry of series) {
    if (entry.kind === "package") {
      if (seen.has(entry.sourceId)) continue;
      seen.add(entry.sourceId);
      items.push({ label: entry.label, color: entry.color, opacity: 1, shape: "rect" });
      continue;
    }
    items.push({
      label: `${entry.label} (${entry.side})`,
      color: entry.color,
      opacity: entry.opacity,
      shape: "rect",
    });
  }
  return items;
}

/**
 * Where the money goes: two stacked columns a year — revenue split by package,
 * cost split by line-item group, with each package's delivery cost carrying its
 * own colour on the cost side.
 *
 * New in this app. The yearly-flows chart says a year cost more than it earned;
 * this one says which package earned it and which line item spent it, which is
 * the question everybody asks second and the one a wrong `basis` shows up in.
 */
export function StackedBreakdownChart({
  model,
  rows,
  currencyCode,
}: {
  model: ScenarioModel;
  rows: YearResult[];
  currencyCode: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const breakdown = useMemo(() => buildBreakdown(model, rows), [model, rows]);

  const isEmpty = breakdown.max <= 0;
  const ticks = axisTicks(breakdown.max);
  const top = axisTop(ticks);
  const yFor = (value: number) => PAD_T + PLOT_H * (1 - value / top);
  const slotW = PLOT_W / Math.max(1, breakdown.years.length);
  const barW = Math.min(STACK_MAX, Math.max(8, (slotW - BAR_GAP) / 2 - 10));
  const groupW = barW * 2 + BAR_GAP;
  const slotCentre = (index: number) => PAD_L + index * slotW + slotW / 2;

  /** Bottom-up, so the first series in draw order sits on the baseline. */
  const stack = (slices: BreakdownSlice[], x: number) => {
    let cursor = 0;
    return slices.map((slice) => {
      const y = yFor(cursor + slice.value);
      const height = PLOT_H * (slice.value / top);
      cursor += slice.value;
      return { slice, x, y, height };
    });
  };

  const hovered = hover !== null ? breakdown.years[hover] : undefined;
  const money = (value: number) => formatMoney(currencyCode, value);

  const tooltipRows = (): TooltipRow[] => {
    if (!hovered) return [];
    const side = (slices: BreakdownSlice[], heading: string, total: number): TooltipRow[] => {
      if (slices.length === 0) return [];
      // Tallest first: the tooltip is read top-down and the biggest slice is the
      // answer to "what is this year", whatever order the bars are drawn in.
      const ordered = [...slices].sort((a, b) => b.value - a.value).slice(0, 5);
      return [
        { label: heading, value: money(total) },
        ...ordered.map((slice) => ({
          label: slice.series.label,
          value: money(slice.value),
          color: slice.series.color,
          opacity: slice.series.opacity,
        })),
      ];
    };
    return [
      ...side(hovered.revenue, "revenue", hovered.revenueTotal),
      ...side(hovered.cost, "cost", hovered.costTotal),
    ];
  };

  return (
    <ChartFrame
      label="Revenue by package and cost by group, stacked for each year"
      overlay={
        <>
          {isEmpty && (
            <ChartEmptyState message="Nothing is earning or costing yet. Add a package price or a cost line to see the split." />
          )}
          {hovered && !isEmpty && (
            <ChartTooltip
              leftPct={tooltipLeftPct(slotCentre(hover ?? 0))}
              title={`Year ${hovered.year}`}
              rows={tooltipRows()}
            />
          )}
        </>
      }
      footer={<ChartLegend items={legendItems(breakdown.series)} />}
    >
      <AxisChrome ticks={ticks} top={top} currencyCode={currencyCode} />

      {!isEmpty &&
        breakdown.years.map((year, index) => {
          const groupX = PAD_L + index * slotW + (slotW - groupW) / 2;
          const bars = [
            ...stack(year.revenue, groupX),
            ...stack(year.cost, groupX + barW + BAR_GAP),
          ];
          return (
            <g key={year.year} opacity={hover === null || hover === index ? 1 : 0.45}>
              {bars.map((bar) => (
                <rect
                  key={bar.slice.series.key}
                  x={bar.x}
                  y={bar.y}
                  width={barW}
                  height={Math.max(0, bar.height)}
                  fill={bar.slice.series.color}
                  opacity={bar.slice.series.opacity}
                  // A hairline in the surface colour separates two slices of the
                  // same tint without inventing a border colour of its own.
                  stroke="var(--surface-strong)"
                  strokeWidth={0.5}
                />
              ))}
            </g>
          );
        })}

      <ZeroLine y={yFor(0)} />

      {breakdown.years.map((year, index) => (
        <g key={year.year}>
          <YearTick x={slotCentre(index)} year={year.year} />
          <HoverSlot
            x={PAD_L + index * slotW}
            width={slotW}
            onEnter={() => setHover(index)}
            onLeave={() => setHover(null)}
          />
        </g>
      ))}
    </ChartFrame>
  );
}
